// creditsPanel.ts — the CREDITS modal (reachable from the AI-disclosure panel + Settings footer).
//
// A short, honest colophon: the geometric player muncher + game wordmark, who made it, the
// AI-content disclosure one-liner (consistent with disclosurePanel.ts — never softened), the
// type/icon/engine credits, the "Original IP" line, and the build version. Pure UI over the shared
// full-bleed modal + kit + tokens; no Core/network. Matches the Tactical OLED Arcade redline.
import Phaser from 'phaser';
import { PALETTE, hex, FONT } from './keys';
import { modal } from './ui';
import { ctaButton, labelText, headingText, cardPanel } from './ui/kit';
import { muncher } from './muncher';
import { scaledFont } from './settings';
import { GAME_VERSION } from '../version';

/** A credit block: an uppercase scan-accent label + a value line, with an optional dim sub-line. */
interface CreditBlock {
  label: string;
  value: string;
  sub?: string;
}

const BLOCKS: readonly CreditBlock[] = [
  { label: 'Design & Engineering', value: 'Dizzy Walk Studio' },
  {
    label: 'Trivia Content',
    value: 'AI-written · independently AI-verified',
    sub: 'Every answer fact-checked before it ships',
  },
  { label: 'Audio', value: 'Synthetic, licensed announcer voice' },
  {
    label: 'Type & Icons',
    value: 'Chakra Petch (OFL) · Phosphor (MIT)',
    sub: 'Built with Phaser · TypeScript · Vite',
  },
];

export function openCredits(scene: Phaser.Scene): ReturnType<typeof modal> {
  const m = modal(scene, {
    title: 'CREDITS',
    fullBleed: true,
    railGrad: [PALETTE.player, PALETTE.rival, PALETTE.gold],
  });
  const { card, cardW, bodyTop } = m;
  const panelW = cardW - 52;
  const wrapW = panelW - 64;

  // — geometric player muncher (cyan chomp + soft halo) then the wordmark —
  let y = bodyTop + 56;
  card.add(muncher(scene, 'player', 0, y, 48));
  y += 64;
  card.add(headingText(scene, 0, y, 'TRIVIA MUNCHERS', { size: 28, origin: 0.5, weight: 'bold' }));
  y += 52;

  // — credit blocks: centred scan label, then value [+ dim sub], all centered. —
  const gridTop = y - 18;
  const gridH = 420;
  card.add(cardPanel(scene, 0, gridTop + gridH / 2, panelW, gridH, { fill: PALETTE.surface, border: PALETTE.lineStrong, accent: PALETTE.scan }));
  for (const b of BLOCKS) {
    card.add(labelText(scene, 0, y, b.label, { origin: 0.5, color: PALETTE.scan, size: 12 }));
    y += 26;
    const val = scene.add
      .text(0, y, b.value, {
        fontFamily: FONT.family,
        fontSize: `${scaledFont(17, 13)}px`,
        color: hex(PALETTE.text),
        align: 'center',
        wordWrap: { width: wrapW },
      })
      .setOrigin(0.5, 0);
    card.add(val);
    y += val.height + 6;
    if (b.sub) {
      const sub = scene.add
        .text(0, y, b.sub, {
          fontFamily: FONT.family,
          fontSize: `${scaledFont(13, 11)}px`,
          color: hex(PALETTE.dim),
          align: 'center',
          wordWrap: { width: wrapW },
        })
        .setOrigin(0.5, 0);
      card.add(sub);
      y += sub.height + 6;
    }
    y += 22;
  }

  // — divider + the Original-IP + version colophon (compliance copy, kept exact) —
  y += 4;
  card.add(scene.add.rectangle(0, y, cardW - 80, 1, PALETTE.line).setOrigin(0.5));
  y += 22;
  card.add(
    scene.add
      .text(0, y, `Original IP — no real-world characters, logos, or trade dress.  ·  v${GAME_VERSION}`, {
        fontFamily: FONT.family,
        fontSize: `${scaledFont(13, 11)}px`,
        color: hex(PALETTE.text),
        align: 'center',
        wordWrap: { width: wrapW },
      })
      .setOrigin(0.5, 0),
  );

  // — DONE (primary green CTA, flowed under the content so the colophon hugs its real height) —
  const ctaY = gridTop + gridH + 58;
  card.add(ctaButton(scene, 0, ctaY, 'Done', () => m.close(), { width: panelW, height: 56 }));
  m.relayout(ctaY + 42 - bodyTop);

  return m;
}
